import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ZoomInIcon, ZoomOutIcon, MoveIcon, EyeIcon, HomeIcon, MonitorIcon, ImageIcon, RefreshCwIcon, CircleIcon, CameraIcon, HomeIcon as RoomViewIcon } from 'lucide-react';
import { CircularToolbar } from './CircularToolbar';
interface KitchenCanvasProps {
  roomData: any;
  selectedUnit: any;
  onUnitRightClick: (e: React.MouseEvent, unit: any) => void;
  onUnitSelect: (unit: any) => void;
  notes?: any[];
}
export function KitchenCanvas({
  roomData,
  selectedUnit,
  onUnitRightClick,
  onUnitSelect,
  notes = []
}: KitchenCanvasProps) {
  const canvasRef = useRef(null);
  const [viewMode, setViewMode] = useState('3d'); // '3d', 'top', 'front', 'side'
  const [showGrid, setShowGrid] = useState(false); // Grid is now hidden by default
  const [scale, setScale] = useState(1);
  const [placedUnits, setPlacedUnits] = useState([]);
  const [draggedUnit, setDraggedUnit] = useState(null);
  const [blackAndWhiteMode, setBlackAndWhiteMode] = useState(false);
  // Track double-click timing
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickedUnit, setLastClickedUnit] = useState(null);
  const [circularToolbar, setCircularToolbar] = useState({
    visible: false,
    x: 0,
    y: 0,
    unit: null
  });
  // Add camera distance state
  const [cameraDistance, setCameraDistance] = useState(50); // 0-100 range
  const [isDraggingCamera, setIsDraggingCamera] = useState(false);
  // Add a ref to store the current circular toolbar state
  const circularToolbarRef = useRef(circularToolbar);
  // Update the ref whenever the state changes
  useEffect(() => {
    circularToolbarRef.current = circularToolbar;
  }, [circularToolbar]);
  // Create stable callback functions using useCallback
  const handleCloseCircularToolbar = useCallback(() => {
    setCircularToolbar(prev => ({
      ...prev,
      visible: false
    }));
  }, []);
  // Fix the callback to not depend on circularToolbar.unit
  const handleCircularToolbarAction = useCallback(action => {
    // Get the unit from the ref instead of closure
    const unit = circularToolbarRef.current.unit;
    if (!unit) return;
    switch (action) {
      case 'rotateLeft':
        console.log('Rotate Left', unit);
        break;
      case 'rotateRight':
        console.log('Rotate Right', unit);
        break;
      case 'duplicate':
        const duplicatedUnit = {
          ...unit,
          id: Date.now(),
          x: unit.x + 20,
          y: unit.y + 20
        };
        setPlacedUnits(prev => [...prev, duplicatedUnit]);
        console.log('Duplicate', unit);
        break;
      case 'group':
        console.log('Group', unit);
        break;
      case 'ungroup':
        console.log('Ungroup', unit);
        break;
      case 'copyStyle':
        console.log('Copy Style', unit);
        break;
      case 'pasteStyle':
        console.log('Paste Style', unit);
        break;
      case 'delete':
        setPlacedUnits(prev => prev.filter(u => u.id !== unit.id));
        console.log('Delete', unit);
        break;
      default:
        console.log('Unknown action:', action);
    }
  }, []);
  // Function to open the circular toolbar from the button
  const openCircularToolbar = () => {
    // Position the toolbar in the center of the canvas
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    setCircularToolbar({
      visible: true,
      x: centerX,
      y: centerY,
      unit: selectedUnit // Use the currently selected unit if available
    });
  };
  // Add camera distance handler
  const handleCameraDistanceChange = newDistance => {
    setCameraDistance(Math.max(0, Math.min(100, newDistance)));
    // Here you would also update the actual 3D camera position
    // This is a placeholder for the actual camera positioning logic
    console.log('Camera distance updated:', newDistance);
  };
  const resetCameraDistance = () => {
    setCameraDistance(50); // Reset to default middle value
  };
  // Handle mouse wheel for camera zoom
  const handleMouseWheel = e => {
    if (e.deltaY !== 0) {
      const direction = e.deltaY > 0 ? -1 : 1; // Invert for natural scrolling
      handleCameraDistanceChange(cameraDistance + direction * 5);
    }
  };
  // Add event listener for mouse wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleMouseWheel);
    return () => {
      canvas.removeEventListener('wheel', handleMouseWheel);
    };
  }, [cameraDistance]);
  useEffect(() => {
    // Initialize canvas and draw room
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Apply black and white filter if enabled
    if (blackAndWhiteMode) {
      ctx.filter = 'grayscale(100%)';
    } else {
      ctx.filter = 'none';
    }
    // Draw grid if enabled
    if (showGrid) {
      drawGrid(ctx, canvas.width, canvas.height);
    }
    // Draw room outline if roomData exists
    if (roomData && roomData.walls) {
      drawRoom(ctx, roomData.walls);
    }
    // Draw placed units
    placedUnits.forEach(unit => {
      drawUnit(ctx, unit);
    });
    // Draw selected unit if being dragged
    if (draggedUnit) {
      drawUnit(ctx, draggedUnit, true);
    }
    // Draw notes
    drawNotes(ctx);
  }, [roomData, selectedUnit, showGrid, viewMode, scale, placedUnits, draggedUnit, notes, blackAndWhiteMode]);
  const drawGrid = (ctx, width, height) => {
    const gridSize = 20 * scale;
    ctx.beginPath();
    ctx.strokeStyle = '#E5E7EB';
    // Vertical lines
    for (let x = 0; x < width; x += gridSize) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    // Horizontal lines
    for (let y = 0; y < height; y += gridSize) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();
  };
  const drawRoom = (ctx, walls) => {
    ctx.beginPath();
    // Start from the first point
    if (walls.length > 0) {
      ctx.moveTo(walls[0].start.x, walls[0].start.y);
      // Draw lines for each wall
      walls.forEach(wall => {
        ctx.lineTo(wall.end.x, wall.end.y);
      });
      // Close the path
      ctx.closePath();
    }
    // Fill with light color
    ctx.fillStyle = 'rgba(243, 244, 246, 0.5)';
    ctx.fill();
    // Draw outline
    ctx.strokeStyle = '#6B7280';
    ctx.lineWidth = 2;
    ctx.stroke();
  };
  const drawUnit = (ctx, unit, isSelected = false) => {
    const {
      x,
      y,
      width,
      height,
      category,
      styling
    } = unit;
    // Different colors based on category and styling
    let fillColor = 'rgba(59, 130, 246, 0.3)'; // default blue
    switch (category) {
      case 'base':
        fillColor = styling === 'modern' ? 'rgba(59, 130, 246, 0.3)' : styling === 'classic' ? 'rgba(245, 158, 11, 0.3)' : styling === 'minimalist' ? 'rgba(255, 255, 255, 0.5)' : styling === 'rustic' ? 'rgba(180, 83, 9, 0.3)' : styling === 'glossy' ? 'rgba(96, 165, 250, 0.5)' : 'rgba(100, 116, 139, 0.3)'; // matte
        break;
      case 'wall':
        fillColor = 'rgba(16, 185, 129, 0.3)'; // green
        break;
      case 'tall':
        fillColor = 'rgba(139, 92, 246, 0.3)'; // purple
        break;
      case 'island':
        fillColor = 'rgba(245, 158, 11, 0.3)'; // amber
        break;
      case 'builtin':
      case 'appliances':
        fillColor = 'rgba(239, 68, 68, 0.3)'; // red
        break;
    }
    // Draw the cabinet
    ctx.fillStyle = fillColor;
    ctx.fillRect(x, y, width, height);
    // Draw border (thicker if selected)
    ctx.strokeStyle = isSelected ? '#3B82F6' : '#6B7280';
    ctx.lineWidth = isSelected ? 3 : 1;
    ctx.strokeRect(x, y, width, height);
    // Draw handles or other details based on the unit type and front configuration
    if (unit.fronts) {
      if (typeof unit.fronts === 'number') {
        // Draw door divisions
        for (let i = 1; i < unit.fronts; i++) {
          const doorX = x + width / unit.fronts * i;
          ctx.beginPath();
          ctx.moveTo(doorX, y);
          ctx.lineTo(doorX, y + height);
          ctx.strokeStyle = '#9CA3AF';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      } else if (typeof unit.fronts === 'string') {
        // Handle mixed door/drawer configurations
        if (unit.fronts.includes('d')) {
          const drawerCount = parseInt(unit.fronts.charAt(unit.fronts.length - 1));
          const doorCount = parseInt(unit.fronts.charAt(0));
          // Draw horizontal line separating doors and drawers
          const drawerSectionHeight = height * 0.3 * drawerCount;
          const doorY = y + height - drawerSectionHeight;
          ctx.beginPath();
          ctx.moveTo(x, doorY);
          ctx.lineTo(x + width, doorY);
          ctx.strokeStyle = '#9CA3AF';
          ctx.lineWidth = 1;
          ctx.stroke();
          // Draw door divisions
          if (doorCount > 1) {
            for (let i = 1; i < doorCount; i++) {
              const doorX = x + width / doorCount * i;
              ctx.beginPath();
              ctx.moveTo(doorX, y);
              ctx.lineTo(doorX, doorY);
              ctx.stroke();
            }
          }
          // Draw drawer divisions
          if (drawerCount > 1) {
            const drawerHeight = drawerSectionHeight / drawerCount;
            for (let i = 1; i < drawerCount; i++) {
              const drawerY = doorY + drawerHeight * i;
              ctx.beginPath();
              ctx.moveTo(x, drawerY);
              ctx.lineTo(x + width, drawerY);
              ctx.stroke();
            }
          }
        }
      }
    }
    // Add label
    ctx.fillStyle = '#1F2937';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(unit.name || 'Cabinet', x + width / 2, y + height / 2);
  };
  // Draw notes on the canvas
  const drawNotes = ctx => {
    if (!notes || notes.length === 0) return;
    notes.forEach(note => {
      // Find the related unit
      const unit = placedUnits.find(u => u.id === note.unitId);
      if (!unit) return;
      const noteX = unit.x + unit.width / 2;
      const noteY = unit.y - 15;
      // Draw circle with number
      ctx.beginPath();
      ctx.arc(noteX, noteY, 10, 0, 2 * Math.PI);
      ctx.fillStyle = '#FF9800';
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Draw number
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(note.number.toString(), noteX, noteY);
      // Draw line to note position if different from unit
      if (note.x && note.y) {
        const distX = note.x - noteX;
        const distY = note.y - noteY;
        const dist = Math.sqrt(distX * distX + distY * distY);
        if (dist > 20) {
          ctx.beginPath();
          ctx.moveTo(noteX, noteY);
          ctx.lineTo(note.x, note.y);
          ctx.strokeStyle = '#FF9800';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    });
  };
  const handleCanvasMouseDown = e => {
    if (e.button === 2) {
      // Right click - prevent default context menu
      e.preventDefault();
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Check if we clicked on an existing unit
      const clickedUnit = placedUnits.find(unit => x >= unit.x && x <= unit.x + unit.width && y >= unit.y && y <= unit.y + unit.height);
      if (clickedUnit) {
        // If we clicked on a unit, pass the event and unit to parent component
        onUnitRightClick(e, clickedUnit);
      } else {
        // If we clicked on empty canvas, pass the event with null unit
        onUnitRightClick(e, null);
      }
      return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Check if we clicked on an existing unit - for left click selection
    const clickedUnit = placedUnits.find(unit => x >= unit.x && x <= unit.x + unit.width && y >= unit.y && y <= unit.y + unit.height);
    if (clickedUnit) {
      // If we clicked on a unit with left click, select it
      if (e.button === 0) {
        // Check for double click
        const currentTime = new Date().getTime();
        const timeDiff = currentTime - lastClickTime;
        if (timeDiff < 300 && lastClickedUnit && lastClickedUnit.id === clickedUnit.id) {
          // Double click detected - show circular toolbar
          setCircularToolbar({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            unit: clickedUnit
          });
        } else {
          // Single click - just select the unit
          onUnitSelect(clickedUnit);
        }
        // Update last click time and unit for double-click detection
        setLastClickTime(currentTime);
        setLastClickedUnit(clickedUnit);
        return;
      }
    } else {
      // Clicked outside any unit - hide circular toolbar
      setCircularToolbar({
        visible: false,
        x: 0,
        y: 0,
        unit: null
      });
    }
    if (!selectedUnit) return;
    // If not clicking on existing unit, create a new one
    if (selectedUnit && e.button === 0 && !clickedUnit) {
      // Left click only
      const newUnit = {
        id: Date.now(),
        x: x - 50,
        y: y - 30,
        width: selectedUnit.width || 100,
        height: selectedUnit.height || 60,
        depth: selectedUnit.depth || 60,
        name: selectedUnit.name,
        category: selectedUnit.category,
        type: selectedUnit.type,
        fronts: selectedUnit.fronts,
        styling: selectedUnit.styling
      };
      setPlacedUnits([...placedUnits, newUnit]);
    }
  };
  const handleCanvasContextMenu = e => {
    // Prevent the default context menu from appearing
    e.preventDefault();
  };
  const handleCanvasMouseMove = e => {
    if (!draggedUnit) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setDraggedUnit({
      ...draggedUnit,
      x: x - draggedUnit.offsetX,
      y: y - draggedUnit.offsetY
    });
  };
  const handleCanvasMouseUp = () => {
    if (draggedUnit) {
      // Add the dragged unit back to the placed units
      const {
        offsetX,
        offsetY,
        ...unitWithoutOffset
      } = draggedUnit;
      setPlacedUnits([...placedUnits, unitWithoutOffset]);
      setDraggedUnit(null);
    }
  };
  const zoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 3));
  };
  const zoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.5));
  };
  const resetCamera = () => {
    setScale(1);
    // If we had camera position state, we would reset it here too
  };
  const toggleBlackAndWhiteMode = () => {
    setBlackAndWhiteMode(!blackAndWhiteMode);
  };
  return <div className="w-full h-full bg-gray-100 relative dark:bg-darkbg-primary" data-id="element-281">
      <canvas ref={canvasRef} className="w-full h-full" onMouseDown={handleCanvasMouseDown} onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp} onMouseLeave={handleCanvasMouseUp} onContextMenu={handleCanvasContextMenu} data-id="element-282"></canvas>

      {/* Circular Menu Button in the top center */}
      <button onClick={openCircularToolbar} className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white rounded-full shadow-md p-2.5 hover:bg-blue-50 z-10 dark:bg-darkbg-secondary dark:border dark:border-darkbg-border dark:hover:bg-darkbg-primary" title="Open Circular Menu" data-id="element-283">
        <CircleIcon className="h-5 w-5 text-blue-600 dark:text-accent-blue" data-id="element-284" />
      </button>

      {/* For testing/demo purposes, let's add a sample unit if none exist */}
      {placedUnits.length === 0 && <button className="absolute top-20 left-20 bg-blue-100 p-2 rounded text-sm" onClick={() => {
      const sampleUnit = {
        id: Date.now(),
        x: 200,
        y: 200,
        width: 120,
        height: 60,
        depth: 60,
        name: 'Sample Cabinet',
        category: 'base',
        type: 'cabinet',
        styling: 'modern'
      };
      setPlacedUnits([sampleUnit]);
    }} data-id="element-285">
          Add Sample Unit
        </button>}

      {/* Circular Toolbar */}
      <CircularToolbar position={{
      x: circularToolbar.x,
      y: circularToolbar.y
    }} visible={circularToolbar.visible} onClose={handleCloseCircularToolbar} onAction={handleCircularToolbarAction} data-id="element-286" />

      {/* View controls */}
      <div className="absolute top-4 left-4 bg-white rounded-md shadow-md p-1 flex flex-col dark:bg-darkbg-secondary dark:border dark:border-darkbg-border" data-id="element-287">
        <button onClick={resetCamera} className="p-2 rounded text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary" title="Reset Camera" data-id="element-288">
          <HomeIcon className="h-4 w-4 mx-auto" data-id="element-289" />
        </button>
        <div className="border-t border-gray-200 my-1 dark:border-darkbg-border" data-id="element-290"></div>
        <button onClick={toggleBlackAndWhiteMode} className={`p-2 rounded ${blackAndWhiteMode ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Black & White Mode" data-id="element-291">
          <MonitorIcon className="h-4 w-4 mx-auto" data-id="element-292" />
        </button>
        <div className="border-t border-gray-200 my-1 dark:border-darkbg-border" data-id="element-293"></div>
        <button onClick={() => setViewMode('3d')} className={`p-2 rounded ${viewMode === '3d' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="3D View" data-id="element-294">
          <span className="text-xs font-medium" data-id="element-295">3D</span>
        </button>
        <button onClick={() => setViewMode('top')} className={`p-2 rounded ${viewMode === 'top' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Top View" data-id="element-296">
          <span className="text-xs font-medium" data-id="element-297">Top</span>
        </button>
        <button onClick={() => setViewMode('front')} className={`p-2 rounded ${viewMode === 'front' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Front View" data-id="element-298">
          <span className="text-xs font-medium" data-id="element-299">Front</span>
        </button>
        <button onClick={() => setViewMode('back')} className={`p-2 rounded ${viewMode === 'back' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Back View" data-id="element-300">
          <span className="text-xs font-medium" data-id="element-301">Back</span>
        </button>
        <button onClick={() => setViewMode('right')} className={`p-2 rounded ${viewMode === 'right' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Right View" data-id="element-302">
          <span className="text-xs font-medium" data-id="element-303">Right</span>
        </button>
        <button onClick={() => setViewMode('left')} className={`p-2 rounded ${viewMode === 'left' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Left View" data-id="element-304">
          <span className="text-xs font-medium" data-id="element-305">Left</span>
        </button>
        {/* Camera Distance Control - Only visible in non-3D and non-TOP views */}
        {viewMode !== '3d' && viewMode !== 'top' && <div className="border-t border-gray-200 my-1 dark:border-darkbg-border" data-id="element-306"></div>}
        {viewMode !== '3d' && viewMode !== 'top' && <div className="relative h-40 mx-auto py-2 flex flex-col items-center" title="Camera Distance" onWheel={e => {
        e.stopPropagation();
        handleMouseWheel(e);
      }} data-id="element-307">
            {/* Camera icon at top */}
            <CameraIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mb-1" data-id="element-308" />
            {/* Vertical slider track */}
            <div className="h-28 w-1.5 bg-gray-200 rounded-full relative dark:bg-darkbg-primary" onWheel={e => {
          e.stopPropagation();
          handleMouseWheel(e);
        }} data-id="element-309">
              {/* Slider fill */}
              <div className="w-full bg-blue-500 dark:bg-accent-blue rounded-full absolute bottom-0" style={{
            height: `${cameraDistance}%`
          }} data-id="element-310"></div>
              {/* Draggable thumb */}
              <div className="absolute w-4 h-4 bg-white border border-gray-300 rounded-full shadow-md transform -translate-x-1/4 cursor-pointer hover:bg-blue-50 dark:bg-darkbg-secondary dark:border-darkbg-border" style={{
            bottom: `calc(${cameraDistance}% - 8px)`
          }} onMouseDown={e => {
            e.preventDefault();
            setIsDraggingCamera(true);
          }} onDoubleClick={resetCameraDistance} onWheel={e => {
            e.stopPropagation();
            handleMouseWheel(e);
          }} data-id="element-311"></div>
            </div>
            {/* Room view icon at bottom */}
            <RoomViewIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-1" data-id="element-312" />
          </div>}
      </div>

      {/* Event handlers for camera distance slider dragging */}
      {isDraggingCamera && <div className="fixed inset-0 z-50 cursor-ns-resize" onMouseMove={e => {
      const sliderRect = e.currentTarget.getBoundingClientRect();
      const sliderHeight = 112; // 28 * 4 (h-28)
      const offsetY = e.clientY - (sliderRect.top + 32); // 32px for top padding and camera icon
      const percentage = 100 - Math.max(0, Math.min(100, offsetY / sliderHeight * 100));
      handleCameraDistanceChange(percentage);
    }} onMouseUp={() => setIsDraggingCamera(false)} onMouseLeave={() => setIsDraggingCamera(false)} data-id="element-313" />}

      {/* Canvas tools */}
      <div className="absolute bottom-4 right-4 bg-white rounded-md shadow-md p-1 flex dark:bg-darkbg-secondary dark:border dark:border-darkbg-border" data-id="element-314">
        <button onClick={zoomIn} className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary dark:hover:text-accent-blue" title="Zoom In" data-id="element-315">
          <ZoomInIcon className="h-5 w-5" data-id="element-316" />
        </button>
        <button onClick={zoomOut} className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary dark:hover:text-accent-blue" title="Zoom Out" data-id="element-317">
          <ZoomOutIcon className="h-5 w-5" data-id="element-318" />
        </button>
        <button className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary dark:hover:text-accent-blue" title="Pan" data-id="element-319">
          <MoveIcon className="h-5 w-5" data-id="element-320" />
        </button>
        <button onClick={() => setShowGrid(!showGrid)} className={`p-2 rounded ${showGrid ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-accent-blue' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-darkbg-primary'}`} title="Toggle Grid" data-id="element-321">
          <div className="h-5 w-5 grid grid-cols-2 grid-rows-2 gap-0.5" data-id="element-322">
            <div className="bg-current rounded-sm" data-id="element-323"></div>
            <div className="bg-current rounded-sm" data-id="element-324"></div>
            <div className="bg-current rounded-sm" data-id="element-325"></div>
            <div className="bg-current rounded-sm" data-id="element-326"></div>
          </div>
        </button>
        <button className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded dark:text-gray-300 dark:hover:bg-darkbg-primary dark:hover:text-accent-blue" title="Toggle Visibility" data-id="element-327">
          <EyeIcon className="h-5 w-5" data-id="element-328" />
        </button>
      </div>

      {/* Measurements */}
      <div className="absolute bottom-4 left-4 bg-white rounded-md shadow-md p-2 dark:bg-darkbg-secondary dark:border dark:border-darkbg-border" data-id="element-329">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-330">
          <span data-id="element-331">Scale: 1:{Math.round(100 / scale)}</span>
          {roomData && roomData.walls && <>
              <span className="mx-2" data-id="element-332">|</span>
              <span data-id="element-333">Room: {roomData.walls.length} walls</span>
            </>}
          {placedUnits.length > 0 && <>
              <span className="mx-2" data-id="element-334">|</span>
              <span data-id="element-335">Units: {placedUnits.length}</span>
            </>}
          {notes.length > 0 && <>
              <span className="mx-2" data-id="element-336">|</span>
              <span data-id="element-337">Notes: {notes.length}</span>
            </>}
        </div>
      </div>

      {/* Instructions if no units are placed */}
      {placedUnits.length === 0 && !roomData?.walls && <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center bg-white bg-opacity-90 p-4 rounded-lg shadow-md dark:bg-darkbg-secondary dark:bg-opacity-90" data-id="element-338">
          <p className="text-lg font-medium text-gray-700 mb-2 dark:text-gray-300" data-id="element-339">
            Select a cabinet type from the left panel
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-340">
            Then drag it to the canvas to place it
          </p>
          <p className="text-sm text-blue-500 mt-2" data-id="element-341">
            Or right-click on existing units to open the radial menu
          </p>
        </div>}
    </div>;
}